import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'October 6, 2026';
const COMPANY = 'Idaho Coders LLC';
const ADDRESS_LINE1 = '800 W Main St Suite 1460';
const ADDRESS_LINE2 = 'Boise, ID 83702';
const CONTACT_EMAIL = 'hello@classroom.bingo';
const SITE = 'classroom.bingo';

export default function TermsPage() {
  return (
    <div style={sty.page}>
      <div style={sty.inner}>
        <div style={sty.backRow}>
          <Link to="/" style={sty.back}>← Back to Classroom Bingo</Link>
        </div>

        <h1 style={sty.h1}>Terms of Service</h1>
        <p style={sty.meta}>Effective: {EFFECTIVE_DATE} &nbsp;·&nbsp; {COMPANY}</p>

        <Section title="1. About Us">
          <p>
            Classroom Bingo is operated by <strong>{COMPANY}</strong>, a limited liability company
            registered in Idaho.
          </p>
          <address style={sty.address}>
            {COMPANY}<br />
            {ADDRESS_LINE1}<br />
            {ADDRESS_LINE2}
          </address>
          <p>
            You may contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={sty.link}>{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="2. Acceptance of Terms">
          <p>
            By creating an account or using {SITE} (the "Service"), you agree to be bound by these
            Terms of Service ("Terms"). If you do not agree, do not use the Service. These Terms
            apply to all users, including teachers and any students who join a game session.
          </p>
        </Section>

        <Section title="3. Eligibility">
          <p>
            The Service is intended for use by K–12 teachers and their students. By creating a
            teacher account, you represent that you are at least 18 years old. Student participants
            do not create accounts and do not provide personal information to the platform beyond a
            first-name display name used for the duration of a single game session.
          </p>
        </Section>

        <Section title="4. Teacher Accounts">
          <p>
            You are responsible for maintaining the confidentiality of your login credentials and
            for all activity under your account. Notify us promptly at {CONTACT_EMAIL} if you
            suspect unauthorized access. We reserve the right to suspend or terminate accounts that
            violate these Terms.
          </p>
        </Section>

        <Section title="5. Subscriptions and Payments">
          <p>
            The Service offers a paid subscription at <strong>$19.99 per year</strong>, billed
            annually through Stripe. Subscriptions <strong>renew automatically</strong> each year
            at the then-current price until cancelled. We email you 30 days and again at least 7
            days before each renewal. You can cancel any time from Account Settings; your access
            continues until the end of the period you have paid for.
          </p>
          <p>
            Certain content (novels unlocked via Teachers Pay Teachers) is available without a
            paid subscription. A subscription grants access to the full novel library and the
            ability to create custom vocabulary lists.
          </p>
          <p>
            All payments are processed by Stripe. {COMPANY} does not store credit card numbers or
            billing details. Receipts are available in your account settings at any time.
          </p>
          <p>
            Subscription fees are <strong>non-refundable</strong> except where required by
            applicable law. If you believe a charge was made in error, contact us within 30 days
            at {CONTACT_EMAIL}.
          </p>
        </Section>

        <Section title="6. Permitted Use">
          <p>You may use the Service only for lawful, educational purposes. You agree not to:</p>
          <ul style={sty.list}>
            <li>Share, resell, or sublicense access to the Service;</li>
            <li>Reverse-engineer, scrape, or copy any part of the Service;</li>
            <li>Upload vocabulary content that is offensive, defamatory, or otherwise harmful;</li>
            <li>Attempt to gain unauthorized access to any part of the Service or its servers.</li>
          </ul>
        </Section>

        <Section title="7. Student Privacy and COPPA / FERPA">
          <p>
            We take student privacy seriously. The Service is designed to minimize data collection
            from minors. Students join game sessions using only a first-name display name; no
            email address, password, or other personal information is collected from students.
            Student display names and board state exist only in session memory and are discarded
            when the game ends.
          </p>
          <p>
            Because the Service collects no persistent personal data from students, we believe it
            is designed consistent with the Children's Online Privacy Protection Act (COPPA) and
            the Family Educational Rights and Privacy Act (FERPA). Teachers are responsible for
            using the Service in compliance with their school or district's policies.
          </p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>
            All platform code, design, and branding are owned by {COMPANY}. Novel vocabulary
            content included in the Service is used for educational purposes. Custom vocabulary
            lists you create remain yours; by uploading them you grant us a limited license to
            store and serve them to your students within the platform.
          </p>
        </Section>

        <Section title="9. Disclaimer of Warranties">
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind,
            express or implied. We do not warrant that the Service will be uninterrupted, error-free,
            or free of harmful components.
          </p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p>
            To the fullest extent permitted by law, {COMPANY} shall not be liable for any indirect,
            incidental, special, or consequential damages arising from your use of the Service. Our
            total liability for any claim arising under these Terms shall not exceed the amount you
            paid us in the twelve months preceding the claim.
          </p>
        </Section>

        <Section title="11. Changes to These Terms">
          <p>
            We may update these Terms from time to time. We will notify you of material changes by
            posting the new Terms on this page and updating the effective date above. Continued use
            of the Service after changes take effect constitutes acceptance of the revised Terms.
          </p>
        </Section>

        <Section title="12. Governing Law">
          <p>
            These Terms are governed by the laws of the State of Idaho, without regard to conflict
            of law principles. Any disputes arising under these Terms shall be resolved in the
            state or federal courts located in Ada County, Idaho.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            Questions about these Terms? Reach us at:
          </p>
          <address style={sty.address}>
            {COMPANY}<br />
            {ADDRESS_LINE1}<br />
            {ADDRESS_LINE2}<br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={sty.link}>{CONTACT_EMAIL}</a>
          </address>
        </Section>

        <div style={sty.footer}>
          <Link to="/privacy" style={sty.link}>Privacy Policy</Link>
          {' · '}
          <Link to="/" style={sty.link}>Back to Classroom Bingo</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={sty.section}>
      <h2 style={sty.h2}>{title}</h2>
      {children}
    </section>
  );
}

const sty = {
  page: {
    minHeight: '100vh', backgroundColor: '#f5f3ef',
    fontFamily: 'system-ui, sans-serif', color: '#1a1a2e',
  },
  inner: {
    maxWidth: '720px', margin: '0 auto', padding: '40px 24px 64px',
  },
  backRow: { marginBottom: '28px' },
  back: { fontSize: '14px', color: '#6b6b7a', textDecoration: 'none' },
  h1: {
    fontSize: '32px', fontWeight: '800', color: '#1a1a2e',
    margin: '0 0 6px', letterSpacing: '-0.3px',
  },
  meta: { fontSize: '13px', color: '#9ca3af', margin: '0 0 36px' },
  section: { marginBottom: '32px' },
  h2: {
    fontSize: '17px', fontWeight: '700', color: '#1a1a2e',
    margin: '0 0 10px', paddingBottom: '6px',
    borderBottom: '1px solid #e5e0d5',
  },
  address: {
    fontStyle: 'normal', lineHeight: '1.7', fontSize: '14px',
    backgroundColor: '#fff', border: '1px solid #e5e0d5',
    borderRadius: '8px', padding: '12px 16px',
    display: 'inline-block', margin: '10px 0',
  },
  list: {
    paddingLeft: '20px', lineHeight: '1.8', fontSize: '15px', color: '#374151',
  },
  link: { color: '#1a1a2e', fontWeight: '600' },
  footer: {
    marginTop: '48px', paddingTop: '20px', borderTop: '1px solid #e5e0d5',
    fontSize: '14px', color: '#6b6b7a',
  },
};
